import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Button } from '../ui/button';
import { Calculator, TrendingUp, Download, Share2, BookOpen, Info } from 'lucide-react';
import { Badge } from '../ui/badge';
import jsPDF from 'jspdf';

const ACRESimulator = () => {
  const [inputs, setInputs] = useState({
    caAnnuel: '50000',
    activite: 'services-bic',
    dateCreation: 'avant-juillet'
  });
  const [results, setResults] = useState({});

  const TAUX = {
    'achat-revente': { normal: 12.3, acreBefore: 6.2, acreAfter: 9.3, label: 'Achat-revente' },
    'services-bic': { normal: 21.2, acreBefore: 10.6, acreAfter: 15.9, label: 'Services BIC' },
    'services-bnc': { normal: 25.6, acreBefore: 12.8, acreAfter: 19.2, label: 'Services BNC' },
    'liberal-cipav': { normal: 23.2, acreBefore: 11.6, acreAfter: 17.4, label: 'Libéral CIPAV' }
  };

  const calculate = () => {
    const ca = parseFloat(inputs.caAnnuel) || 0;
    const taux = TAUX[inputs.activite];
    const tauxAcre = inputs.dateCreation === 'avant-juillet' ? taux.acreBefore : taux.acreAfter;

    const cotisationsNormales = ca * (taux.normal / 100);
    const cotisationsACRE = ca * (tauxAcre / 100);
    const economie = cotisationsNormales - cotisationsACRE;
    const economieMensuelle = economie / 12;
    const pourcentageEconomie = cotisationsNormales > 0 ? (economie / cotisationsNormales * 100) : 0;

    setResults({
      cotisationsNormales: Math.round(cotisationsNormales),
      cotisationsACRE: Math.round(cotisationsACRE),
      economie: Math.round(economie),
      economieMensuelle: Math.round(economieMensuelle),
      pourcentageEconomie: pourcentageEconomie.toFixed(0),
      tauxNormal: taux.normal,
      tauxAcre,
      revenuNetNormal: Math.round(ca - cotisationsNormales),
      revenuNetACRE: Math.round(ca - cotisationsACRE)
    });
  };

  useEffect(() => { calculate(); }, [inputs]);

  const handleInputChange = (field, value) => {
    setInputs(prev => ({ ...prev, [field]: value }));
  };

  const shareLink = () => {
    const url = window.location.href;
    if (navigator.share) { navigator.share({ title: 'Simulateur ACRE - Bozzam', url }); }
    else { navigator.clipboard.writeText(url); alert('Lien copié !'); }
  };

  const exportPDF = () => {
    const doc = new jsPDF();
    doc.setFillColor(30, 64, 175); doc.rect(0, 0, 210, 40, 'F');
    doc.setTextColor(255); doc.setFontSize(20);
    doc.text('Bozzam - ACRE Gain Réel', 15, 25);
    doc.setFontSize(10); doc.text('blog.bozzam.com', 15, 35);
    doc.setTextColor(0); let y = 55;
    doc.setFontSize(11);
    doc.text(`CA annuel : ${parseInt(inputs.caAnnuel).toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Activité : ${TAUX[inputs.activite].label}`, 15, y); y += 7;
    doc.text(`Cotisations sans ACRE : ${results.cotisationsNormales?.toLocaleString()} EUR (${results.tauxNormal}%)`, 15, y); y += 7;
    doc.text(`Cotisations avec ACRE : ${results.cotisationsACRE?.toLocaleString()} EUR (${results.tauxAcre}%)`, 15, y); y += 7;
    doc.text(`Économie totale : ${results.economie?.toLocaleString()} EUR`, 15, y); y += 7;
    doc.text(`Soit ${results.economieMensuelle?.toLocaleString()} EUR/mois`, 15, y); y += 15;
    doc.setFontSize(9); doc.setTextColor(100);
    doc.text('Sources : URSSAF 2026, Décret ACRE, entreprendre.service-public.fr', 15, y);
    doc.save('simulation-acre-bozzam.pdf');
  };

  return (
    <div className="max-w-6xl mx-auto p-6">
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <Card className="h-fit">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Calculator className="h-5 w-5 text-blue-600" /> ACRE - Gain réel
            </CardTitle>
            <CardDescription>Calculez combien l'ACRE vous fait économiser en 2026</CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div>
              <Label>CA annuel prévu (€)</Label>
              <Input data-testid="ca-input" type="text" inputMode="numeric" value={inputs.caAnnuel}
                onChange={(e) => handleInputChange('caAnnuel', e.target.value.replace(/[^0-9]/g, ''))}
                placeholder="50000" className="mt-1" />
            </div>
            <div>
              <Label>Type d'activité</Label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {Object.entries(TAUX).map(([key, val]) => (
                  <Button key={key} data-testid={`activite-${key}-btn`}
                    variant={inputs.activite === key ? 'default' : 'outline'}
                    className={`text-left justify-start text-sm ${inputs.activite === key ? 'bg-blue-600 hover:bg-blue-700' : ''}`}
                    onClick={() => handleInputChange('activite', key)}>
                    {val.label} ({val.normal}% → {inputs.dateCreation === 'avant-juillet' ? val.acreBefore : val.acreAfter}%)
                  </Button>
                ))}
              </div>
            </div>
            <div>
              <Label>Date de création</Label>
              <div className="grid grid-cols-2 gap-2 mt-2">
                <Button data-testid="date-avant-btn"
                  variant={inputs.dateCreation === 'avant-juillet' ? 'default' : 'outline'}
                  className={inputs.dateCreation === 'avant-juillet' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  onClick={() => handleInputChange('dateCreation', 'avant-juillet')}>
                  Avant juillet 2026 (50%)
                </Button>
                <Button data-testid="date-apres-btn"
                  variant={inputs.dateCreation === 'apres-juillet' ? 'default' : 'outline'}
                  className={inputs.dateCreation === 'apres-juillet' ? 'bg-blue-600 hover:bg-blue-700' : ''}
                  onClick={() => handleInputChange('dateCreation', 'apres-juillet')}>
                  Après juillet 2026 (25%)
                </Button>
              </div>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-start gap-2">
                <BookOpen className="h-4 w-4 text-blue-600 mt-0.5" />
                <div className="text-sm text-blue-800">
                  <p className="font-medium mb-1">Sources légales 2026</p>
                  <ul className="list-disc list-inside text-xs space-y-1">
                    <li>ACRE : 50% d'exonération jusqu'au 30/06/2026</li>
                    <li>Décret n°2026-69 : passage à 25% dès 01/07/2026</li>
                    <li>Durée : 4 trimestres civils à compter de la création</li>
                    <li>entreprendre.service-public.fr - urssaf.fr</li>
                  </ul>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-600" /> Résultats
            </CardTitle>
            <Badge className="bg-green-100 text-green-800">
              ACRE {inputs.dateCreation === 'avant-juillet' ? '50%' : '25%'}
            </Badge>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid grid-cols-2 gap-3">
              <div className="p-4 bg-red-50 rounded-lg text-center">
                <p className="text-xs text-red-600 mb-1">Sans ACRE ({results.tauxNormal}%)</p>
                <p className="text-xl font-bold text-red-900">{results.cotisationsNormales?.toLocaleString()}€</p>
              </div>
              <div className="p-4 bg-green-50 rounded-lg text-center border-2 border-green-200">
                <p className="text-xs text-green-600 mb-1">Avec ACRE ({results.tauxAcre}%)</p>
                <p className="text-xl font-bold text-green-900" data-testid="result-acre">{results.cotisationsACRE?.toLocaleString()}€</p>
              </div>
            </div>

            <div className="p-6 bg-gradient-to-r from-green-50 to-emerald-50 rounded-lg border-2 border-green-300 text-center">
              <p className="text-sm text-gray-600 mb-1">Économie grâce à l'ACRE</p>
              <p className="text-3xl font-black text-green-900" data-testid="result-economie">{results.economie?.toLocaleString()}€</p>
              <p className="text-sm text-green-700">soit {results.economieMensuelle?.toLocaleString()}€/mois | -{results.pourcentageEconomie}% de cotisations</p>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div className="p-3 bg-gray-50 rounded-lg text-center">
                <p className="text-xs text-gray-600 mb-1">Revenu net sans ACRE</p>
                <p className="text-lg font-bold text-gray-900">{results.revenuNetNormal?.toLocaleString()}€</p>
              </div>
              <div className="p-3 bg-green-50 rounded-lg text-center border border-green-200">
                <p className="text-xs text-green-600 mb-1">Revenu net avec ACRE</p>
                <p className="text-lg font-bold text-green-900">{results.revenuNetACRE?.toLocaleString()}€</p>
              </div>
            </div>

            <div className="grid grid-cols-3 gap-3">
              <Button onClick={shareLink} variant="outline" data-testid="share-btn"><Share2 className="h-4 w-4 mr-2" /> Partager</Button>
              <Button onClick={exportPDF} variant="outline" data-testid="export-btn"><Download className="h-4 w-4 mr-2" /> PDF</Button>
              <Button className="bg-blue-600 hover:bg-blue-700" onClick={() => window.open(process.env.REACT_APP_BOZZAM_APP_URL, '_blank')}>Bozzam App</Button>
            </div>
            <div className="text-xs text-gray-500 text-center pt-2 border-t">
              <p>Sources : URSSAF 2026 - Décret n°2026-69 - service-public.fr</p>
              <p>lamicrobyflo.fr | entreprendre.service-public.fr</p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default ACRESimulator;
